import { useState } from 'react';
import {
  BUNDLE_VERSION,
  applyNow,
  checkNow,
  liveUpdateEnabled,
  useUpdateStatus,
  type CheckResult,
} from '../lib/liveUpdate';

type State = 'idle' | 'checking' | 'applying' | 'failed' | CheckResult;

/**
 * 版本号 + 「检查更新」。
 *
 * 平时不用点 —— 每次回到前台都会自己查、自己在后台下载、切后台时换上。
 * 这个按钮是给「调度说发了新版、我现在就要」的时候用的:
 * 下好了就给一个「立即更新」,当场换上。
 *
 * ★ 网页版不显示按钮:浏览器里刷新就是最新,没有「装在本地的旧包」。
 */
export function UpdateCheck() {
  const [checked, setState] = useState<State>('idle');
  /*
   * ★ 后台自己查到、下好的新版本,这里也要认 —— 不然提示条说「新版本已就绪」,
   *   点进来这里还是一个「检查更新」,让人以为是两回事。
   */
  const status = useUpdateStatus();
  const state: State =
    checked === 'checking' || checked === 'applying'
      ? checked
      : status.kind === 'ready'
        ? 'staged'
        : status.kind === 'reinstall'
          ? 'native-too-old'
          : checked;

  const check = async () => {
    setState('checking');
    try {
      setState(await checkNow());
    } catch {
      setState('failed');
    }
  };

  const apply = async () => {
    setState('applying');
    try {
      await applyNow(); // 成功的话页面已经重载,走不到下面
    } catch {
      setState('failed');
    }
  };

  const msg: Partial<Record<State, string>> = {
    'up-to-date': '已是最新版本',
    // 坏过的版本不会再装,对司机来说手上这个就是「最新可用」的
    'known-bad': '已是最新版本',
    'native-too-old': '新版本需要重新安装 App,请联系调度',
    'bad-manifest': '检查失败,请稍后再试',
    failed: '检查失败,请检查网络后重试',
  };

  return (
    <div className="text-center text-xs text-slate-400 space-y-2">
      {/* 司机报问题时,调度一问就知道他手上是哪一版(热更新之后不看这里分不清) */}
      <p>版本 {BUNDLE_VERSION}</p>

      {liveUpdateEnabled &&
        (state === 'staged' || state === 'applying' ? (
          <div className="space-y-2">
            <p className="text-slate-600 dark:text-slate-300">新版本已下载</p>
            <button
              onClick={apply}
              disabled={state === 'applying'}
              className="h-11 px-6 rounded-xl bg-brand-600 text-white text-sm font-medium disabled:opacity-60"
            >
              {state === 'applying' ? '正在更新…' : '立即更新'}
            </button>
            <p>不点也行,切到后台再回来就会自动换上</p>
          </div>
        ) : (
          <div className="space-y-1">
            <button
              onClick={check}
              disabled={state === 'checking'}
              className="text-sm text-brand-600 dark:text-brand-400 disabled:opacity-60"
            >
              {state === 'checking' ? '正在检查…' : '检查更新'}
            </button>
            {msg[state] && <p>{msg[state]}</p>}
          </div>
        ))}
    </div>
  );
}
